import { useCallback, useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, Crown, DownloadSimple, Heart, Stack } from "@phosphor-icons/react";
import LazyImage from "../LazyImage";
import { EASE } from "./motion";

function RailCard({ pack, rank, onOpen, dragged }) {
  const title = pack.character_name || pack.title;
  return (
    <motion.div
      className="flex items-end flex-shrink-0 snap-start"
      initial={{ opacity: 0, y: 70 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "0px 0px -8% 0px" }}
      transition={{ duration: 0.9, delay: Math.min(rank - 1, 4) * 0.08, ease: EASE }}
    >
      <span
        aria-hidden="true"
        className="text-outline font-display font-black leading-[0.8] text-[7rem] md:text-[10rem] -mr-5 md:-mr-8 mb-1 relative z-0 select-none"
      >
        {rank}
      </span>
      <motion.button
        type="button"
        onClick={() => { if (!dragged.current) onOpen(pack); }}
        data-testid={`rail-card-${pack.id}`}
        whileHover={{ y: -10 }}
        whileTap={{ scale: 0.98 }}
        transition={{ type: "spring", stiffness: 320, damping: 26 }}
        className="group relative z-10 block w-[52vw] sm:w-[210px] md:w-[250px] aspect-[2/3] rounded-2xl overflow-hidden text-left border border-white/10 hover:border-white/30 bg-[#1A1A1A] shadow-[0_18px_40px_-18px_rgba(0,0,0,0.8)] hover:shadow-[0_28px_60px_-16px_rgba(136,0,21,0.55)] transition-[border-color,box-shadow] duration-500"
      >
        <LazyImage
          src={pack.thumbnail_url}
          width={640}
          alt={title}
          className="absolute inset-0 w-full h-full"
          imgClassName="w-full h-full object-cover transition-transform duration-700 ease-out will-change-transform transform-gpu group-hover:scale-[1.07]"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/95 via-black/25 to-transparent" />
        <div className="absolute inset-x-0 top-0 h-1/3 bg-gradient-to-b from-black/45 to-transparent" />
        {/* light sweep on hover */}
        <div className="absolute inset-0 -translate-x-full group-hover:translate-x-full transition-transform duration-700 ease-out bg-gradient-to-r from-transparent via-white/10 to-transparent skew-x-12" />

        <div className="absolute top-3 left-3 flex gap-1.5">
          {(pack.qualities?.length ? pack.qualities.slice(0, 2) : [pack.quality]).filter(Boolean).map((q) => (
            <span key={q} className="chip chip-garnet !py-1 !px-2.5 !text-[10px] backdrop-blur-md">{q}</span>
          ))}
          {pack.premium && <span className="chip chip-garnet !py-1 !px-2 backdrop-blur-md"><Crown size={10} weight="fill" /></span>}
        </div>

        <div className="absolute bottom-0 left-0 right-0 p-4">
          <div className="text-[10px] font-mono uppercase tracking-[0.22em] text-white/55 mb-1 truncate">
            {pack.movie_title}{pack.year ? ` · ${pack.year}` : ""}
          </div>
          <div className="font-display font-black text-xl md:text-2xl leading-[1.05] uppercase tracking-tight line-clamp-2">{title}</div>
          <div className="mt-3 flex items-center justify-between text-[11px] font-mono text-white/60">
            <span className="flex items-center gap-3">
              <span className="flex items-center gap-1"><DownloadSimple size={12} /> {pack.downloads}</span>
              <span className="flex items-center gap-1"><Heart size={12} /> {pack.likes}</span>
              {pack.versions > 1 && <span className="flex items-center gap-1" title="Versions"><Stack size={12} /> {pack.versions}</span>}
            </span>
            <span className="w-8 h-8 rounded-full bg-white text-black flex items-center justify-center opacity-0 translate-y-2 group-hover:opacity-100 group-hover:translate-y-0 transition duration-300">
              <ArrowRight size={14} weight="bold" />
            </span>
          </div>
        </div>
      </motion.button>
    </motion.div>
  );
}

function Skeleton() {
  return (
    <div className="flex gap-6 overflow-hidden px-6 md:px-12">
      {Array.from({ length: 5 }).map((_, i) => (
        <div key={i} className="w-[250px] aspect-[2/3] rounded-2xl bg-white/[0.04] animate-pulse flex-shrink-0" />
      ))}
    </div>
  );
}

/** Horizontal, drag-or-arrow "Top 10" poster rail with numbered ranks. */
export default function TrendingRail({ packs, loading, onOpen }) {
  const track = useRef(null);
  const bar = useRef(null);
  const dragged = useRef(false);
  const drag = useRef(null);
  const [edge, setEdge] = useState({ start: true, end: false });

  const sync = useCallback(() => {
    const el = track.current;
    if (!el) return;
    const max = el.scrollWidth - el.clientWidth;
    const p = max > 0 ? el.scrollLeft / max : 0;
    const visible = el.clientWidth / el.scrollWidth;
    if (bar.current) {
      bar.current.style.width = `${Math.max(12, visible * 100)}%`;
      bar.current.style.transform = `translateX(${p * (100 / Math.max(visible, 0.01) - 100)}%)`;
    }
    setEdge((e) => (e.start === (el.scrollLeft < 8) && e.end === (el.scrollLeft > max - 8) ? e : { start: el.scrollLeft < 8, end: el.scrollLeft > max - 8 }));
  }, []);

  useEffect(() => {
    sync();
    window.addEventListener("resize", sync);
    return () => window.removeEventListener("resize", sync);
  }, [sync, packs]);

  const nudge = (dir) => track.current?.scrollBy({ left: dir * track.current.clientWidth * 0.8, behavior: "smooth" });

  // mouse drag-to-scroll (touch already scrolls natively)
  const onPointerDown = (e) => {
    if (e.pointerType !== "mouse" || e.button !== 0) return;
    drag.current = { x: e.clientX, left: track.current.scrollLeft };
    dragged.current = false;
  };
  const onPointerMove = (e) => {
    if (!drag.current) return;
    const dx = e.clientX - drag.current.x;
    if (Math.abs(dx) > 6) {
      dragged.current = true;
      track.current.classList.add("is-dragging");
    }
    if (dragged.current) track.current.scrollLeft = drag.current.left - dx;
  };
  const endDrag = () => {
    if (!drag.current) return;
    drag.current = null;
    track.current?.classList.remove("is-dragging");
    setTimeout(() => { dragged.current = false; }, 0);
  };

  const top = (packs || []).slice(0, 10);

  return (
    <div className="relative">
      <div className="flex items-center justify-between max-w-[1600px] mx-auto px-6 md:px-12 mb-2">
        <div className="h-px flex-1 bg-white/10 mr-6 hidden md:block" />
        <div className="flex gap-2">
          {[[-1, ArrowLeft, edge.start], [1, ArrowRight, edge.end]].map(([d, Icon, off]) => (
            <button
              key={d}
              type="button"
              onClick={() => nudge(d)}
              disabled={off}
              aria-label={d < 0 ? "Previous" : "Next"}
              className="w-11 h-11 rounded-full border border-white/15 hover:border-white/40 hover:bg-white/5 disabled:opacity-25 disabled:pointer-events-none flex items-center justify-center transition"
            >
              <Icon size={16} />
            </button>
          ))}
        </div>
      </div>

      {loading ? <Skeleton /> : (
        <div
          ref={track}
          onScroll={sync}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={endDrag}
          onPointerLeave={endDrag}
          data-testid="trending-rail"
          className="rail-track flex gap-5 md:gap-8 overflow-x-auto scrollbar-hide snap-x snap-mandatory px-6 md:px-12 pt-6 pb-10 cursor-grab"
          style={{ scrollPaddingLeft: "max(1.5rem, calc((100vw - 1600px) / 2 + 3rem))" }}
        >
          {top.map((p, i) => <RailCard key={p.id} pack={p} rank={i + 1} onOpen={onOpen} dragged={dragged} />)}
          <div className="flex-shrink-0 w-4 md:w-12" />
        </div>
      )}

      <div className="max-w-[1600px] mx-auto px-6 md:px-12">
        <div className="h-[3px] rounded-full bg-white/10 overflow-hidden">
          <div ref={bar} className="h-full rounded-full bg-gradient-to-r from-[#880015] to-[#FF8090] will-change-transform" style={{ width: "30%" }} />
        </div>
      </div>
    </div>
  );
}
